import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { IoIosAddCircle } from "react-icons/io";
import "./AddModulePopup.css";
import Notification from "../Notification/Notification";
import { useAuth } from "../../Context/AuthContext";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const AddModulePopup = ({ programId, setModules, modules }) => {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const [modalShow, setModalShow] = useState(false);
  const { authData } = useAuth();
  const [importModalShow, setImportModalShow] = useState(false);
  const [selectedModules, setSelectedModules] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [notificationParams, setNotificationParams] = useState({
    show: false,
    message: t("notification_default_message"),
    bg: "success",
  });
  const [importOption, setImportOption] = useState("restore");

  const handleModuleSelect = (moduleId) => {
    setSelectedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((id) => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleImportSubmit = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/modules/restore`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ moduleIds: selectedModules }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("error_occurred"));
      }

      setModules((prev) => [...prev, ...data.restoredModules]);

      setNotificationParams((prev) => ({
        show: true,
        message: data.message,
        bg: "success",
      }));

      setImportModalShow(false);
    } catch (err) {
      console.error(err);
      setError(err.message || t("error_occurred"));
      setNotificationParams((prev) => ({
        show: true,
        message: err.message,
        bg: "danger",
      }));
    }
  };

  const handleDuplicateSubmit = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/modules/${selectedModules[0]}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ programId: programId }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || t("error_occurred"));
      }

      setModules((prev) => [...prev, data.newModule]);

      setImportModalShow(false);

      setNotificationParams((prev) => ({
        show: true,
        message: data.message,
        bg: "success",
      }));
    } catch (err) {
      setError(err.message);
      console.error(err);
      setNotificationParams((prev) => ({
        show: true,
        message: err.message,
        bg: "danger",
      }));
    }
    setImportModalShow(false);
  };

  const handleModalShow = () => {
    setModalShow(true);
    setImportModalShow(false);
  };

  const handleImportModalShow = async () => {
    setModalShow(false);
    setImportModalShow(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/modules`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t("error_occurred"));
      }

      setAllModules(data.modules);
    } catch (err) {
      console.error(err);
    }

    setSelectedModules([]);
  };

  const handleTitleChange = (e) => setTitle(e.target.value);
  const handleDescriptionChange = (e) => setDescription(e.target.value);

  const handleSubmit = async () => {
    setError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/modules`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            title: title,
            description: description,
            program: programId,
            is_visible: true,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || t("error_occurred"));
      }

      setNotificationParams((prev) => ({
        show: true,
        message: data.message,
        bg: "success",
      }));

      setModules((prev) => [...prev, data.module]);
      setTitle("");
      setDescription("");
    } catch (err) {
      console.error(err);
      setError(err.message);
      setNotificationParams((prev) => ({
        show: true,
        message: err.message,
        bg: "danger",
      }));
      setTitle("");
      setDescription("");
    }

    setModalShow(false);
  };

  const getModulesToDisplay = () => {
    if (importOption === "restore") {
      return allModules.filter((mod) => mod.is_deleted === true);
    } else if (importOption === "otherProgram") {
      return allModules.filter(
        (mod) =>
          mod.program._id.toString() !== programId.toString() &&
          !modules.some(
            (existingModule) =>
              existingModule.title === mod.title + " -- CLONE --"
          ) &&
          mod.is_deleted === false &&
          (mod.program.created_by._id === authData.userId ||
            mod.program.is_importable === true)
      );
    }
    return [];
  };

  return (
    <>
      <div onClick={handleModalShow} className="add-module-container">
        <IoIosAddCircle className="icon" />
        <span>{t("add_module")}</span>
      </div>

      <Modal
        show={modalShow}
        onHide={() => {
          setModalShow(false);
          setError("");
        }}
        centered
        className="modal-custom"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("create_module")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formName">
              <Form.Label>{t("module_title")}</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={handleTitleChange}
                placeholder={t("enter_module_title")}
                className="input-field"
              />
            </Form.Group>

            <Form.Group controlId="formDescription" className="mt-3">
              <Form.Label>{t("module_description")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={description}
                onChange={handleDescriptionChange}
                placeholder={t("enter_module_description")}
                className="input-field"
              />
            </Form.Group>

            <div className="button-container mt-3 d-flex justify-content-between">
              <Button onClick={handleImportModalShow}>
                {t("import_module_question")}
              </Button>
              <Button
                onClick={handleSubmit}
                className="btn-submit"
                disabled={title === "" || description === ""}
              >
                {t("create_module")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <Modal
        show={importModalShow}
        onHide={() => setImportModalShow(false)}
        centered
        className="modal-custom"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("import_module")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group
              controlId="formImportOption"
              className="options-container"
            >
              <Form.Label>{t("choose_import_option")}</Form.Label>
              <div className="d-flex gap-4 ">
                <Form.Check
                  type="radio"
                  label={t("restore_deleted_module")}
                  name="importOption"
                  value="restore"
                  checked={importOption === "restore"}
                  onChange={(e) => {
                    setSelectedModules([]);
                    setError("");
                    setImportOption(e.target.value);
                  }}
                />
                <Form.Check
                  type="radio"
                  label={t("import_module_other_program")}
                  name="importOption"
                  value="otherProgram"
                  checked={importOption === "otherProgram"}
                  onChange={(e) => {
                    setSelectedModules([]);
                    setError("");
                    setImportOption(e.target.value);
                  }}
                />
              </div>
              {selectedModules.length > 1 &&
                importOption === "otherProgram" && (
                  <div className="text-center mb-2 text-danger">
                    {t("duplicate_one_module")}
                  </div>
                )}
            </Form.Group>
            {error && <div className="text-danger mt-2">{error}</div>}
            <div className="modules-container">
              {getModulesToDisplay().map((module) => (
                <Form.Group key={module._id} controlId={`module-${module._id}`}>
                  <Form.Check
                    type="checkbox"
                    label={`${module.title} - ${module.description}`}
                    checked={selectedModules.includes(module._id)}
                    onChange={() => handleModuleSelect(module._id)}
                    className="w-100"
                  />
                </Form.Group>
              ))}
            </div>

            <div className="button-container mt-3 d-flex justify-content-between">
              <Button onClick={handleModalShow}>
                {t("create_module_question")}
              </Button>
              <Button
                onClick={
                  importOption === "restore"
                    ? handleImportSubmit
                    : handleDuplicateSubmit
                }
                className="btn-submit"
                disabled={
                  selectedModules.length === 0 ||
                  (importOption === "otherProgram" &&
                    selectedModules.length !== 1)
                }
              >
                {t("import_modules")}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <Notification
        notificationParams={notificationParams}
        setNotificationParams={setNotificationParams}
      />
    </>
  );
};

export default AddModulePopup;
